import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Maximize,
  Minimize,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Volume1,
  Volume2,
  VolumeX,
} from 'lucide-react';

/** Video tugaganda ko'rsatiladigan keyingi dars */
export interface NextLesson {
  title: string;
  href: string;
}

interface Props {
  video: HTMLVideoElement | null;
  /** To'liq ekran bo'ladigan konteyner (watermark ham ichida) — klaviatura shu yerda tinglanadi */
  box: HTMLDivElement | null;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
  /** Player hali tayyor emas yoki xato — panel yashiriladi */
  disabled: boolean;
  /** Pozitsiya eslab qolingan joydan boshlandi (soniya) — "davom ettirildi" xabari uchun */
  resumedFrom: number | null;
  next?: NextLesson;
}

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const SPEED_KEY = 'sqluz-video-speed';
const VOLUME_KEY = 'sqluz-video-volume';
const HIDE_AFTER_MS = 2500;
const SEEK_STEP = 10;
const DOUBLE_TAP_MS = 300;

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const s = Math.floor(sec % 60);
  const m = Math.floor((sec / 60) % 60);
  const h = Math.floor(sec / 3600);
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

function readNumber(key: string, fallback: number): number {
  try {
    const v = Number(localStorage.getItem(key));
    return localStorage.getItem(key) != null && Number.isFinite(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

function writeNumber(key: string, value: number) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* private rejim */
  }
}

/**
 * HlsPlayer uchun o'z boshqaruv paneli (brauzerning native controls o'rniga): hamma brauzerda bir
 * xil ko'rinish, tezlik, ±10 s, klaviatura, mobil'da ikki marta bosish, keyingi dars.
 */
export function PlayerControls({ video, box, fullscreen, onToggleFullscreen, disabled, resumedFrom, next }: Props) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const [ended, setEnded] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [visible, setVisible] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hover, setHover] = useState<{ x: number; time: number } | null>(null);
  const [scrubbing, setScrubbing] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'play' | 'pause' | 'back' | 'fwd'; id: number } | null>(null);
  const [resumeNote, setResumeNote] = useState<number | null>(null);
  const hideTimer = useRef<number>();
  const trackRef = useRef<HTMLDivElement>(null);
  const lastTap = useRef<{ t: number; x: number } | null>(null);
  const tapTimer = useRef<number>();
  const lastPointerType = useRef<string>('mouse');
  const menuRef = useRef<HTMLDivElement>(null);

  // Tezlik menyusi: tashqariga bosish yoki Esc bilan yopiladi
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Video holatini kuzatish
  useEffect(() => {
    if (!video) return;
    const v = video;
    const savedRate = readNumber(SPEED_KEY, 1);
    if (SPEEDS.includes(savedRate)) v.playbackRate = savedRate;
    v.volume = Math.min(1, Math.max(0, readNumber(VOLUME_KEY, 1)));

    const sync = () => {
      setPlaying(!v.paused && !v.ended);
      setTime(v.currentTime);
      setDuration(Number.isFinite(v.duration) ? v.duration : 0);
      setVolume(v.volume);
      setMuted(v.muted);
      setRate(v.playbackRate);
      setEnded(v.ended);
    };
    const onProgress = () => {
      const b = v.buffered;
      let end = 0;
      for (let i = 0; i < b.length; i++) {
        if (b.start(i) <= v.currentTime + 0.5) end = Math.max(end, b.end(i));
      }
      setBuffered(end);
    };
    const onWaiting = () => setWaiting(true);
    const onPlaying = () => {
      setWaiting(false);
      sync();
    };
    const events: [string, () => void][] = [
      ['play', sync], ['pause', sync], ['ended', sync], ['timeupdate', sync], ['durationchange', sync],
      ['loadedmetadata', sync], ['volumechange', sync], ['ratechange', sync], ['seeked', sync],
      ['progress', onProgress], ['timeupdate', onProgress], ['waiting', onWaiting], ['playing', onPlaying],
      ['canplay', () => setWaiting(false)],
      // Pauzada vaqt o'zgartirilsa 'playing' kelmaydi — spinner seeked/loadeddata da o'chadi
      ['seeked', () => setWaiting(false)],
      ['loadeddata', () => setWaiting(false)],
    ];
    events.forEach(([e, fn]) => v.addEventListener(e, fn));
    sync();
    return () => events.forEach(([e, fn]) => v.removeEventListener(e, fn));
  }, [video]);

  useEffect(() => {
    if (resumedFrom == null) return;
    setResumeNote(resumedFrom);
    const id = window.setTimeout(() => setResumeNote(null), 6000);
    return () => window.clearTimeout(id);
  }, [resumedFrom]);

  // Panel: harakatsiz qolganda ijro paytida yashirinadi
  const poke = useCallback(() => {
    setVisible(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setVisible(false), HIDE_AFTER_MS);
  }, []);
  useEffect(() => () => window.clearTimeout(hideTimer.current), []);
  const showControls = !playing || visible || menuOpen || scrubbing || ended;

  const showFlash = (kind: 'play' | 'pause' | 'back' | 'fwd') => setFlash({ kind, id: Date.now() });
  // Animatsiya to'xtab qolsa ham (fon tab, reduced motion) belgi ekranda qolmasin
  useEffect(() => {
    if (!flash) return;
    const id = window.setTimeout(() => setFlash(null), 650);
    return () => window.clearTimeout(id);
  }, [flash]);

  const togglePlay = useCallback(() => {
    if (!video) return;
    if (video.paused || video.ended) {
      void video.play().catch(() => undefined);
      showFlash('play');
    } else {
      video.pause();
      showFlash('pause');
    }
  }, [video]);

  const seekBy = useCallback(
    (delta: number) => {
      if (!video || !Number.isFinite(video.duration)) return;
      video.currentTime = Math.min(Math.max(0, video.currentTime + delta), video.duration - 0.1);
      showFlash(delta < 0 ? 'back' : 'fwd');
      poke();
    },
    [video, poke],
  );

  const seekTo = (sec: number) => {
    if (!video || !duration) return;
    video.currentTime = Math.min(Math.max(0, sec), duration - 0.1);
  };

  const setVol = (v: number) => {
    if (!video) return;
    video.volume = Math.min(1, Math.max(0, v));
    video.muted = video.volume === 0;
    writeNumber(VOLUME_KEY, video.volume);
  };

  const toggleMute = () => {
    if (!video) return;
    if (video.muted || video.volume === 0) {
      video.muted = false;
      if (video.volume === 0) video.volume = 0.5;
    } else {
      video.muted = true;
    }
  };

  const setSpeed = (r: number) => {
    if (!video) return;
    video.playbackRate = r;
    writeNumber(SPEED_KEY, r);
    setMenuOpen(false);
  };

  // Vaqt chizig'i: sichqoncha/barmoq bilan sudrash
  const timeAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || !duration) return { x: 0, time: 0 };
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return { x: ratio * rect.width, time: ratio * duration };
  };
  const onTrackDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!duration) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setScrubbing(true);
    const p = timeAt(e.clientX);
    setHover(p);
    seekTo(p.time);
  };
  const onTrackMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const p = timeAt(e.clientX);
    setHover(p);
    if (scrubbing) seekTo(p.time);
  };
  const onTrackUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    setScrubbing(false);
  };

  // Video maydoniga bosish: desktop — play/pause; mobil — panelni ko'rsatish, ikki marta — ±10 s
  const onSurfacePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    lastPointerType.current = e.pointerType;
    if (e.pointerType === 'mouse') {
      togglePlay();
      return;
    }
    const now = Date.now();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const prev = lastTap.current;
    if (prev && now - prev.t < DOUBLE_TAP_MS && Math.abs(prev.x - x) < 0.2 && (x < 0.35 || x > 0.65)) {
      window.clearTimeout(tapTimer.current);
      lastTap.current = null;
      seekBy(x < 0.5 ? -SEEK_STEP : SEEK_STEP);
      return;
    }
    lastTap.current = { t: now, x };
    window.clearTimeout(tapTimer.current);
    tapTimer.current = window.setTimeout(() => {
      if (showControls && playing) setVisible(false);
      else poke();
    }, DOUBLE_TAP_MS);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (disabled || e.altKey || e.ctrlKey || e.metaKey) return;
    const target = e.target as HTMLElement;
    if (target.closest('[role="menu"]') && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) return;
    const k = e.key.toLowerCase();
    let handled = true;
    if (k === ' ' || k === 'k') {
      if (target.tagName === 'BUTTON' && k === ' ') return; // tugmaning o'zi bosiladi
      togglePlay();
    } else if (k === 'arrowleft') seekBy(-5);
    else if (k === 'arrowright') seekBy(5);
    else if (k === 'j') seekBy(-SEEK_STEP);
    else if (k === 'l') seekBy(SEEK_STEP);
    else if (k === 'arrowup') setVol((video?.volume ?? 1) + 0.1);
    else if (k === 'arrowdown') setVol((video?.volume ?? 1) - 0.1);
    else if (k === 'm') toggleMute();
    else if (k === 'f') onToggleFullscreen();
    else if (/^[0-9]$/.test(k) && duration) seekTo((Number(k) / 10) * duration);
    else handled = false;
    if (handled) {
      e.preventDefault();
      poke();
    }
  };

  // Klaviatura konteynerda tinglanadi — tashqi komponent box ga onKeyDown bera olmaydi, shuning uchun ulaymiz
  const keyHandler = useRef(onKeyDown);
  keyHandler.current = onKeyDown;
  useEffect(() => {
    if (!box) return;
    const fn = (e: KeyboardEvent) => keyHandler.current(e as unknown as ReactKeyboardEvent<HTMLDivElement>);
    box.addEventListener('keydown', fn);
    const onMove = () => poke();
    box.addEventListener('pointermove', onMove);
    return () => {
      box.removeEventListener('keydown', fn);
      box.removeEventListener('pointermove', onMove);
    };
  }, [box, poke]);

  if (disabled) return null;

  const played = duration ? (time / duration) * 100 : 0;
  const loaded = duration ? (Math.min(buffered, duration) / duration) * 100 : 0;
  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  const centerSide =
    'pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white ring-1 ring-white/15 backdrop-blur-sm transition hover:scale-110 hover:bg-black/75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white sm:h-12 sm:w-12';
  const btn = 'flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-md text-white/90 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/70';

  return (
    <>
      {/* Bosish maydoni (panel ostida) */}
      <div
        className="absolute inset-0"
        onPointerUp={onSurfacePointerUp}
        onDoubleClick={() => lastPointerType.current === 'mouse' && onToggleFullscreen()}
        aria-hidden
      />

      {/* Yuklanish */}
      {waiting && !ended && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="h-12 w-12 animate-spin rounded-full border-4 border-white/20 border-t-white" />
        </div>
      )}

      {/* Markaziy belgi (play/pause/±10 s) */}
      {/* Panel ko'ringanda markazda tugmalar bor — belgi faqat panel yashiringanda (klaviatura, ikki marta bosish) */}
      {flash && !showControls && (
        <div key={flash.id} className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span
            className={`flex h-16 w-16 items-center justify-center rounded-full bg-black/55 text-white animate-player-flash ${
              flash.kind === 'back' ? '-translate-x-[30%]' : flash.kind === 'fwd' ? 'translate-x-[30%]' : ''
            }`}
          >
            {flash.kind === 'play' && <Play className="ml-1 h-7 w-7" fill="currentColor" />}
            {flash.kind === 'pause' && <Pause className="h-7 w-7" fill="currentColor" />}
            {flash.kind === 'back' && <span className="text-sm font-bold">−{SEEK_STEP}s</span>}
            {flash.kind === 'fwd' && <span className="text-sm font-bold">+{SEEK_STEP}s</span>}
          </span>
        </div>
      )}

      {/* Markaziy boshqaruv: −10 s · play/pauza · +10 s (panel ko'ringanda) */}
      {showControls && !ended && (
        <div className="pointer-events-none absolute left-1/2 top-[42%] flex -translate-x-1/2 -translate-y-1/2 items-center gap-6 sm:top-1/2 sm:gap-10">
          <button
            type="button"
            onClick={() => seekBy(-SEEK_STEP)}
            aria-label={t('courses.player.back10')}
            title={t('courses.player.back10')}
            className={centerSide}
          >
            <SkipIcon direction="back" />
          </button>
          {waiting ? (
            <span className="h-12 w-12 sm:h-16 sm:w-16" aria-hidden />
          ) : (
            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? t('courses.player.pause') : t('courses.player.play')}
              className="pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg transition-transform hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white sm:h-16 sm:w-16"
            >
              {playing ? (
                <Pause className="h-6 w-6 sm:h-7 sm:w-7" fill="currentColor" />
              ) : (
                <Play className="ml-1 h-6 w-6 sm:h-7 sm:w-7" fill="currentColor" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => seekBy(SEEK_STEP)}
            aria-label={t('courses.player.forward10')}
            title={t('courses.player.forward10')}
            className={centerSide}
          >
            <SkipIcon direction="forward" />
          </button>
        </div>
      )}

      {/* Tugadi: qayta ko'rish / keyingi dars */}
      {ended && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/75 p-6 text-center text-white">
          {next ? (
            <>
              <p className="text-xs font-medium uppercase tracking-wider text-white/60">{t('courses.player.upNext')}</p>
              <p className="max-w-md text-lg font-semibold leading-snug">{next.title}</p>
              <div className="flex flex-wrap justify-center gap-3">
                <button type="button" onClick={togglePlay} className="inline-flex items-center gap-2 rounded-md bg-white/15 px-4 py-2 text-sm font-semibold hover:bg-white/25">
                  <RotateCcw className="h-4 w-4" />
                  {t('courses.player.replay')}
                </button>
                <Link to={next.href} className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold hover:bg-brand-500">
                  {t('courses.player.nextLesson')}
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </>
          ) : (
            <button type="button" onClick={togglePlay} className="inline-flex items-center gap-2 rounded-md bg-white/15 px-4 py-2 text-sm font-semibold hover:bg-white/25">
              <RotateCcw className="h-4 w-4" />
              {t('courses.player.replay')}
            </button>
          )}
        </div>
      )}

      {/* "Davom ettirildi" xabari */}
      {resumeNote != null && !ended && (
        <div className="absolute left-3 top-3 flex items-center gap-3 rounded-md bg-black/70 px-3 py-2 text-xs text-white shadow">
          <span>{t('courses.player.resumed', { time: formatTime(resumeNote) })}</span>
          <button
            type="button"
            onClick={() => {
              seekTo(0);
              setResumeNote(null);
            }}
            className="font-semibold text-brand-300 hover:text-brand-200"
          >
            {t('courses.player.restart')}
          </button>
        </div>
      )}

      {/* Pastki panel */}
      <div
        className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-3 pb-2 pt-10 transition-opacity duration-200 sm:px-4 ${
          showControls ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onPointerMove={poke}
      >
        {/* Vaqt chizig'i */}
        <div
          ref={trackRef}
          role="slider"
          tabIndex={0}
          aria-label={t('courses.player.seek')}
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(time)}
          aria-valuetext={`${formatTime(time)} / ${formatTime(duration)}`}
          className="group relative flex h-4 cursor-pointer touch-none items-center focus-visible:outline-none"
          onPointerDown={onTrackDown}
          onPointerMove={onTrackMove}
          onPointerUp={onTrackUp}
          onPointerLeave={() => !scrubbing && setHover(null)}
        >
          <div className="relative h-1 w-full overflow-hidden rounded-full bg-white/25 transition-[height] group-hover:h-1.5 group-focus-visible:h-1.5">
            <div className="absolute inset-y-0 left-0 bg-white/40" style={{ width: `${loaded}%` }} />
            <div className="absolute inset-y-0 left-0 bg-brand-500" style={{ width: `${played}%` }} />
          </div>
          <div
            className={`pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow transition-transform ${
              scrubbing ? 'scale-100' : 'scale-0 group-hover:scale-100 group-focus-visible:scale-100'
            }`}
            style={{ left: `${played}%` }}
          />
          {hover && duration > 0 && (
            <div
              className="pointer-events-none absolute bottom-5 -translate-x-1/2 rounded bg-black/85 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-white"
              style={{ left: hover.x }}
            >
              {formatTime(hover.time)}
            </div>
          )}
        </div>

        {/* Tugmalar */}
        <div className="mt-1 flex items-center gap-1 text-white">
          <button type="button" className={btn} onClick={togglePlay} aria-label={playing ? t('courses.player.pause') : t('courses.player.play')}>
            {playing ? <Pause className="h-5 w-5" fill="currentColor" /> : <Play className="h-5 w-5" fill="currentColor" />}
          </button>
          <button type="button" className={`${btn} max-sm:hidden`} onClick={() => seekBy(-SEEK_STEP)} aria-label={t('courses.player.back10')} title={t('courses.player.back10')}>
            <RotateCcw className="h-[18px] w-[18px]" />
          </button>
          <button type="button" className={`${btn} max-sm:hidden`} onClick={() => seekBy(SEEK_STEP)} aria-label={t('courses.player.forward10')} title={t('courses.player.forward10')}>
            <RotateCw className="h-[18px] w-[18px]" />
          </button>

          <div className="group/vol flex items-center">
            <button type="button" className={btn} onClick={toggleMute} aria-label={muted || volume === 0 ? t('courses.player.unmute') : t('courses.player.mute')}>
              <VolumeIcon className="h-5 w-5" />
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              onChange={(e) => setVol(Number(e.target.value))}
              aria-label={t('courses.player.volume')}
              className="hidden h-1 w-0 cursor-pointer accent-white opacity-0 transition-all duration-200 group-hover/vol:w-20 group-hover/vol:opacity-100 focus-visible:w-20 focus-visible:opacity-100 sm:block"
            />
          </div>

          <span className="ml-2 whitespace-nowrap text-xs font-medium tabular-nums text-white/90 sm:text-[13px]">
            {formatTime(time)} <span className="text-white/50">/ {formatTime(duration)}</span>
          </span>

          <div className="ml-auto flex items-center gap-1">
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                className={`${btn} w-auto px-2 text-xs font-semibold tabular-nums`}
                onClick={() => setMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={t('courses.player.speed')}
                title={t('courses.player.speed')}
              >
                {rate === 1 ? '1×' : `${rate}×`}
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  aria-label={t('courses.player.speed')}
                  className="absolute bottom-11 right-0 min-w-[7.5rem] overflow-hidden rounded-lg bg-black/90 py-1 text-sm shadow-xl ring-1 ring-white/10"
                >
                  <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wider text-white/50">{t('courses.player.speed')}</p>
                  {SPEEDS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      role="menuitemradio"
                      aria-checked={rate === s}
                      onClick={() => setSpeed(s)}
                      className={`flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-white/10 ${rate === s ? 'font-semibold text-brand-300' : 'text-white/90'}`}
                    >
                      {s === 1 ? t('courses.player.normalSpeed') : `${s}×`}
                      {rate === s && <span aria-hidden>✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className={btn}
              onClick={onToggleFullscreen}
              aria-label={fullscreen ? t('courses.player.exitFullscreen') : t('courses.player.fullscreen')}
              title={fullscreen ? t('courses.player.exitFullscreen') : t('courses.player.fullscreen')}
            >
              {fullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/** Aylana strelka ichida "10" — ±10 soniya tugmasi */
function SkipIcon({ direction }: { direction: 'back' | 'forward' }) {
  const Icon = direction === 'back' ? RotateCcw : RotateCw;
  return (
    <span className="relative flex items-center justify-center">
      <Icon className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={1.75} />
      <span className="absolute pt-px text-[9px] font-bold leading-none sm:text-[10px]">{SEEK_STEP}</span>
    </span>
  );
}
